import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Send, Sparkles, Cpu, User, Plus, ArrowRight, MapPin, Package, Trash2, AlertTriangle } from 'lucide-react';
import { createClient } from '@supabase/supabase-js';
import SEO from '../components/SEO';
import { track, EVENTS } from '../lib/analytics';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const supabase = SUPABASE_URL && SUPABASE_KEY ? createClient(SUPABASE_URL, SUPABASE_KEY) : null;

const STORAGE_KEY = 'giwahs_assistant_chat_v1';
const STREAM_INTERVAL_MS = 14;

const SUGGESTIONS = [
  'I need 500 hoodies in heavyweight fleece for the EU market — which factories should I shortlist?',
  'Compare denim sourcing in Portugal vs Bangladesh for a 1,000-unit run.',
  'Which of your factories hold GOTS or OEKO-TEX certifications for organic cotton tees?',
  'Help me draft an RFQ for a premium streetwear capsule — 300 units, 6 SKUs.',
];

const newSessionId = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

const loadStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.session_id || !Array.isArray(parsed?.messages)) return null;
    return parsed;
  } catch { return null; }
};

const titleCase = (s) => (s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const AIAssistant = () => {
  const navigate = useNavigate();
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]); // {role,content,slugs?,action?,streaming?,fullContent?}
  const [input, setInput] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(null);
  const [supplierMap, setSupplierMap] = useState({}); // slug -> supplier row
  const scrollRef = useRef(null);
  const streamTimers = useRef([]);

  // ── Hydrate on mount ─────────────────────────────────────────────
  useEffect(() => {
    const stored = loadStored();
    if (stored) {
      setSessionId(stored.session_id);
      setMessages(stored.messages);
    } else {
      const sid = newSessionId();
      setSessionId(sid);
      setMessages([{
        role: 'assistant',
        content: "Welcome to the GIWAHS Advisor — your sourcing copilot. I'm grounded on the live GIWAHS factory database and trained on apparel, MOQ strategy, certifications, country sourcing, and RFQ drafting. Ask me anything, or pick a starter below.",
      }]);
      track(EVENTS.ASSISTANT_CHAT_STARTED, { session_id: sid });
    }
  }, []);

  // ── Persist conversation ─────────────────────────────────────────
  useEffect(() => {
    if (!sessionId) return;
    try {
      const compact = messages.map(({ streaming, fullContent, ...rest }) => rest);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ session_id: sessionId, messages: compact }));
    } catch { /* localStorage quota — ignore */ }
  }, [messages, sessionId]);

  // ── Auto-scroll ──────────────────────────────────────────────────
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  // ── Cleanup streaming timers on unmount ─────────────────────────
  useEffect(() => () => streamTimers.current.forEach(clearTimeout), []);

  // ── Fetch supplier details for grounded slugs ───────────────────
  const allSlugs = useMemo(() => {
    const set = new Set();
    messages.forEach((m) => (m.slugs || []).forEach((s) => set.add(s)));
    return Array.from(set);
  }, [messages]);

  useEffect(() => {
    if (!supabase) return;
    const missing = allSlugs.filter((s) => !supplierMap[s]);
    if (missing.length === 0) return;
    (async () => {
      const { data, error: err } = await supabase
        .from('suppliers')
        .select('slug,name,city,country_slug,type_slug,trust_score,moq_min,tier')
        .in('slug', missing);
      if (err || !data) return;
      setSupplierMap((prev) => {
        const next = { ...prev };
        data.forEach((s) => { next[s.slug] = s; });
        return next;
      });
    })();
  }, [allSlugs, supplierMap]);

  // ── Streaming reveal ─────────────────────────────────────────────
  const streamReply = (idx, fullText) => {
    let i = 0;
    const step = () => {
      i = Math.min(i + 3, fullText.length);
      setMessages((prev) => {
        const copy = [...prev];
        if (!copy[idx]) return prev;
        copy[idx] = { ...copy[idx], content: fullText.slice(0, i), streaming: i < fullText.length };
        return copy;
      });
      if (i < fullText.length) {
        const t = setTimeout(step, STREAM_INTERVAL_MS);
        streamTimers.current.push(t);
      }
    };
    step();
  };

  // ── Send message ─────────────────────────────────────────────────
  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || pending) return;
    setError(null);
    setInput('');

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setPending(true);
    track(EVENTS.ASSISTANT_MESSAGE_SENT, {
      message_length: content.length,
      from_suggestion: !!text,
      turn_index: nextMessages.filter((m) => m.role === 'user').length,
    });

    // Build history (exclude system seed welcome message → just user/assistant)
    const history = messages
      .filter((m) => m.role === 'user' || (m.role === 'assistant' && m.content && !m.streaming))
      .slice(-20)
      .map((m) => ({ role: m.role, content: m.fullContent || m.content }));

    const started = Date.now();
    try {
      const resp = await fetch(`${BACKEND}/api/assistant/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId, message: content, history }),
      });
      const data = await resp.json().catch(() => null);
      if (!resp.ok) {
        const msg = data?.detail || `Assistant error (HTTP ${resp.status})`;
        throw new Error(msg);
      }

      const replyText = (data.reply || '').trim();
      const assistantMsg = {
        role: 'assistant',
        content: '',
        fullContent: replyText,
        slugs: data.grounded_supplier_slugs || [],
        action: data.suggested_action || null,
        streaming: true,
      };

      setMessages((prev) => {
        const copy = [...prev, assistantMsg];
        // start streaming on the new index
        const idx = copy.length - 1;
        setTimeout(() => streamReply(idx, replyText), 50);
        return copy;
      });
      track(EVENTS.ASSISTANT_REPLY_RECEIVED, {
        reply_length: replyText.length,
        grounded_count: (data.grounded_supplier_slugs || []).length,
        action_kind: data.suggested_action?.kind || null,
        elapsed_ms: Date.now() - started,
        model: data.model,
      });
    } catch (e) {
      const reason = e?.message || 'Unknown error';
      setError(reason);
      track(EVENTS.ASSISTANT_REPLY_FAILED, { reason });
    } finally {
      setPending(false);
    }
  };

  // ── New chat ─────────────────────────────────────────────────────
  const newChat = () => {
    streamTimers.current.forEach(clearTimeout);
    streamTimers.current = [];
    const sid = newSessionId();
    setSessionId(sid);
    setMessages([{
      role: 'assistant',
      content: 'New chat started. What would you like to source?',
    }]);
    setError(null);
    setInput('');
    try { localStorage.removeItem(STORAGE_KEY); } catch {/*ignore*/}
    track(EVENTS.ASSISTANT_CHAT_CLEARED, { session_id: sid });
  };

  // ── Action handlers ──────────────────────────────────────────────
  const onSupplierClick = (slug, placement) => {
    track(EVENTS.ASSISTANT_SUPPLIER_VIEWED, { supplier_slug: slug, placement });
  };

  const onStartRfq = (payload) => {
    const q = new URLSearchParams();
    if (payload?.category) q.set('prefill_category', payload.category);
    if (payload?.brief) q.set('prefill_brief', payload.brief);
    track(EVENTS.ASSISTANT_RFQ_CREATED, { category: payload?.category || null });
    navigate(`/rfq?${q.toString()}`);
  };

  const onCompare = (slugs) => {
    track(EVENTS.ASSISTANT_COMPARE_OPENED, { count: slugs.length });
    navigate(`/compare?suppliers=${slugs.join(',')}`);
  };

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div data-testid="assistant-page" className="bg-[#0A0A0A] text-white">
      <SEO
        title="GIWAHS Advisor — Claude-powered Sourcing Copilot"
        description="Ask anything about factories, MOQ, sourcing regions, certifications, or RFQ drafting. Grounded on the live GIWAHS factory database."
      />
      <section className="border-b border-neutral-800">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 label-overline" style={{ color: '#C8A96B' }}>
                <Cpu size={14} strokeWidth={1.5} /> / GIWAHS Advisor
              </div>
              <h1 className="mt-4 font-display tracking-tight" style={{ fontWeight: 600, fontSize: 'clamp(2.4rem, 5.6vw, 4.8rem)', lineHeight: 1.02 }}>
                Ask anything<br />about <em className="giwahs-gold-emphasis" style={{ fontStyle: 'italic' }}>sourcing.</em>
              </h1>
              <p className="mt-6 max-w-xl" style={{ color: '#D9D9D9' }}>
                Identify suppliers, compare regions, estimate MOQ, draft RFQs — your AI sourcing copilot, grounded on our live factory database.
              </p>
              <div className="mt-4 inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-emerald-300">
                <span className="label-overline">Live</span>
                <span className="font-mono-data text-[11px]">Powered by Claude Sonnet 4.5 · grounded on GIWAHS factory database</span>
              </div>
            </div>
            <button
              onClick={newChat}
              data-testid="assistant-new-chat"
              className="btn-giwahs btn-giwahs-outline-gold giwahs-new-chat-btn hidden md:inline-flex items-center gap-2"
            >
              <Plus size={14} strokeWidth={1.5} className="giwahs-new-chat-plus" /> New chat
            </button>
          </div>
        </div>
      </section>

      <section className="bg-[#0A0A0A]">
        <div className="mx-auto max-w-3xl px-5 py-10 md:px-8 md:py-14">
          <div className="border border-white/20 bg-[#151515]" data-testid="assistant-chat-container">
            <div className="flex items-center justify-between border-b border-white/20 px-5 py-3">
              <span className="label-overline text-neutral-400">Conversation</span>
              <button onClick={newChat} data-testid="assistant-clear-btn" className="md:hidden inline-flex items-center gap-1 text-xs text-neutral-400 hover:text-white">
                <Trash2 size={12} strokeWidth={1.5} /> Clear
              </button>
            </div>
            <div ref={scrollRef} className="max-h-[520px] min-h-[360px] overflow-y-auto p-5 md:p-7 space-y-5">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`} data-testid={`msg-${i}`} data-role={m.role}>
                  <div className={`flex h-8 w-8 shrink-0 items-center justify-center border ${m.role === 'user' ? 'border-white bg-white text-black' : 'border-white/40 text-white'}`}>
                    {m.role === 'user' ? <User size={14} strokeWidth={1.5} /> : <Sparkles size={14} strokeWidth={1.5} />}
                  </div>
                  <div className={`flex-1 max-w-[85%] ${m.role === 'user' ? '' : ''}`}>
                    <div className={`border p-4 text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'border-white bg-white text-black' : 'border-white/20 bg-[#0A0A0A] text-neutral-200'}`}>
                      {m.content}
                      {m.streaming && <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-white" aria-hidden />}
                    </div>

                    {/* Grounded supplier cards */}
                    {m.role === 'assistant' && (m.slugs || []).length > 0 && (
                      <div className="mt-3 grid gap-2" data-testid={`msg-${i}-suppliers`}>
                        {m.slugs.map((slug) => {
                          const s = supplierMap[slug];
                          return (
                            <Link
                              key={slug}
                              to={`/supplier/${slug}`}
                              onClick={() => onSupplierClick(slug, 'inline-card')}
                              data-testid={`assistant-supplier-${slug}`}
                              className="block border border-white/30 bg-[#111] p-3 hover:bg-[#181818] hover:border-white/60 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="label-overline text-emerald-400">DB-grounded</span>
                                    {s?.tier && <span className="font-mono-data text-[10px] text-neutral-500">· {s.tier}</span>}
                                  </div>
                                  <div className="mt-1 font-display text-base tracking-tight truncate" style={{ fontWeight: 600 }}>{s?.name || slug}</div>
                                  <div className="mt-1 flex items-center gap-3 font-mono-data text-[11px] text-neutral-400">
                                    {s?.country_slug && (<span className="inline-flex items-center gap-1"><MapPin size={11} strokeWidth={1.5} /> {titleCase(s.country_slug)}</span>)}
                                    {s?.moq_min != null && (<span className="inline-flex items-center gap-1"><Package size={11} strokeWidth={1.5} /> MOQ {s.moq_min}+</span>)}
                                    {s?.trust_score != null && (<span>trust {s.trust_score}/100</span>)}
                                  </div>
                                </div>
                                <ArrowRight size={14} strokeWidth={1.5} className="shrink-0 text-neutral-400" />
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Suggested action button */}
                    {m.role === 'assistant' && m.action && !m.streaming && (
                      <div className="mt-3 flex flex-wrap gap-2" data-testid={`msg-${i}-actions`}>
                        {m.action.kind === 'start_rfq' && (
                          <button
                            onClick={() => onStartRfq(m.action.payload)}
                            data-testid="assistant-action-rfq"
                            className="inline-flex items-center gap-2 border border-white bg-white px-4 py-2 text-xs text-black hover:bg-transparent hover:text-white transition-colors"
                          >
                            <Sparkles size={12} strokeWidth={1.5} /> Generate RFQ from this
                          </button>
                        )}
                        {m.action.kind === 'compare_suppliers' && Array.isArray(m.action.payload?.slugs) && m.action.payload.slugs.length >= 2 && (
                          <button
                            onClick={() => onCompare(m.action.payload.slugs)}
                            data-testid="assistant-action-compare"
                            className="inline-flex items-center gap-2 border border-white px-4 py-2 text-xs text-white hover:bg-white hover:text-black transition-colors"
                          >
                            <ArrowRight size={12} strokeWidth={1.5} /> Compare these {m.action.payload.slugs.length} factories
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {pending && (
                <div className="flex gap-3" data-testid="assistant-typing">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center border border-white/40">
                    <Sparkles size={14} strokeWidth={1.5} />
                  </div>
                  <div className="flex gap-1 border border-white/20 bg-[#0A0A0A] p-4">
                    <span className="h-1.5 w-1.5 animate-pulse bg-white" />
                    <span className="h-1.5 w-1.5 animate-pulse bg-white" style={{ animationDelay: '120ms' }} />
                    <span className="h-1.5 w-1.5 animate-pulse bg-white" style={{ animationDelay: '240ms' }} />
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-white/20 p-5 md:p-7">
              {error && (
                <div className="mb-3 inline-flex items-start gap-2 border border-red-500/40 bg-red-500/10 px-3 py-2 text-red-300" data-testid="assistant-error">
                  <AlertTriangle size={14} strokeWidth={1.5} className="mt-0.5" />
                  <span className="font-mono-data text-[12px]">{error}</span>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    onClick={() => send(s)}
                    disabled={pending}
                    data-testid={`suggestion-${i}`}
                    className="rounded-full border border-white/30 px-4 py-1.5 text-xs text-neutral-300 hover:bg-white hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
                <input
                  data-testid="assistant-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask about factories, MOQ, sourcing regions, certifications…"
                  maxLength={4000}
                  disabled={pending}
                  className="flex-1 border border-white/30 bg-transparent px-3 py-3 text-sm placeholder:text-neutral-500 focus:border-white focus:outline-none disabled:opacity-50"
                />
                <button type="submit" disabled={pending || !input.trim()} data-testid="assistant-send" className="flex items-center justify-center border border-white bg-white px-4 text-black hover:bg-transparent hover:text-white disabled:opacity-40">
                  <Send size={14} strokeWidth={1.6} />
                </button>
              </form>
              <p className="mt-3 font-mono-data text-[10px] text-neutral-500">LIVE · Claude Sonnet 4.5 · Grounded on the GIWAHS factory database — supplier records are real, general guidance is AI-assisted.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIAssistant;
